// Concept frames — pre-curated "corners of recipe space" the AI
// generator samples from before drafting.
//
// Background: with `temperature: 1` already maxed and a typical Italian-
// staple pantry, Claude's modal answer to "be creative for me" IS Cacio
// e Pepe. We can't fix that with a soft-language nudge — the training
// distribution heavily associates the pantry shape with the iconic
// dishes. Instead we pre-pick a CONCEPT FRAME (a recipe type plus
// optional cuisine/technique/hero/vibe) and ask Claude to draft the
// most interesting answer INSIDE that frame. Claude's job becomes
// "be a great cook for THIS corner," not "find me a creative dish."
// The randomness lives here, where we control it.
//
// Five axes:
//   type      — REQUIRED on full draws. Sets the recipe form: plate
//               main, sauce, condiment, baked good, ferment, etc.
//               Always populated unless the user's typed prompt named
//               a SPECIFIC dish (in which case the dish IS the form).
//   cuisine   — optional. Deep-cut regional only — "Sicilian," not
//               "Italian." Bare names are exactly what trains Claude
//               to default to canonical answers; specifics break out.
//   technique — optional. Under-used moves — confit, en croûte,
//               brûlée, hand-rolled, fermented, smoked. Each has an
//               `onlyTypes` filter when it doesn't make sense across
//               every recipe type (laminated only fits baked goods).
//   hero      — optional. Spices, ferments, and pantry deep-cuts
//               Claude reaches for last by default — sumac, fenugreek,
//               miso, anchovy, browned butter. Plus signal-based
//               heroes computed at draw time from pantry signals
//               (an expiring ingredient becomes a forced hero).
//   vibe      — optional. When/why context — late-night, Sunday-
//               morning project, thunderstorm comfort, heat-wave cool.
//
// On every draw, type is sampled first, then each optional axis rolls
// a coin (probabilities in AXIS_ROLL). Drawn axes pick weighted-random
// from their library, filtered to entries compatible with the chosen
// type.
//
// Anti-rotation:
//   - Recent frame IDs are tracked in localStorage (last 30, key
//     `ai.frames.recent.v1:${userId}`). The picker excludes them and
//     retries once when it lands on a recent ID.
//   - Top-3 cuisines from the user's cook history are excluded from
//     cuisine candidates so the AI can't keep landing in the same
//     regional rut the user is already cooking out of.
//
// Per the user's no-emoji standing direction, axis entries carry only
// `id`, `label`, `weight`, and (for techniques) `onlyTypes`. The
// reveal card renders typographically — no emoji decoration.

// ── Recipe types ───────────────────────────────────────────────────
// PRIMARY axis. Always populated unless we're in SPECIFIC sub-frame
// mode (user typed a specific dish; the dish IS the type).
export const RECIPE_TYPES = [
  { id: "plate_main",         label: "A plated main dish",                  weight: 0.9 },
  { id: "plate_side",         label: "A side dish",                         weight: 0.6 },
  { id: "soup_stew",          label: "A soup or stew",                      weight: 0.7 },
  { id: "salad",              label: "A salad",                             weight: 0.6 },
  { id: "sauce",              label: "A sauce",                             weight: 0.7 },
  { id: "condiment",          label: "A condiment",                         weight: 0.6 },
  { id: "marinade_brine",     label: "A marinade or brine",                 weight: 0.4 },
  { id: "spice_blend",        label: "A spice blend or rub",                weight: 0.5 },
  { id: "compound_flavoring", label: "A compound butter, oil, or vinegar",  weight: 0.5 },
  { id: "ferment_pickle",     label: "A ferment or pickle",                 weight: 0.6 },
  { id: "jam_preserve",       label: "A jam or preserve",                   weight: 0.5 },
  { id: "stock_broth",        label: "A stock or broth",                    weight: 0.4 },
  { id: "baked_sweet",        label: "A sweet baked good",                  weight: 0.7 },
  { id: "baked_savory",       label: "A savory bread or baked good",        weight: 0.6 },
  { id: "cookie",             label: "A cookie",                            weight: 0.6 },
  { id: "candy_confection",   label: "A candy or confection",               weight: 0.5 },
  { id: "frozen_treat",       label: "A frozen treat",                      weight: 0.5 },
  { id: "snack_bar",          label: "A snack or bar",                      weight: 0.5 },
  { id: "beverage",           label: "A drink or infusion",                 weight: 0.5 },
  { id: "dumpling",           label: "A dumpling or filled-dough form",     weight: 0.6 },
  { id: "egg_hero",           label: "An egg-hero dish",                    weight: 0.6 },
  { id: "raw_prep",           label: "A raw or no-cook preparation",        weight: 0.4 },
  { id: "appetizer_small",    label: "An appetizer or small bite",          weight: 0.5 },
  { id: "breakfast_pastry",   label: "A breakfast pastry",                  weight: 0.5 },
  { id: "savory_tart",        label: "A savory tart or galette",            weight: 0.5 },
];

// Types that don't fit a strict-pantry constraint without specialty
// inputs. When `mode === "pantry"`, these are filtered out of the
// candidate pool so the user doesn't get a frame they can't execute.
const PANTRY_INCOMPATIBLE_TYPES = new Set([
  "frozen_treat", "dumpling", "candy_confection",
  "baked_sweet", "savory_tart", "breakfast_pastry",
]);

// ── Cuisines ───────────────────────────────────────────────────────
// Deep-cut regional. Bare "Italian" / "French" / "Mexican" are
// deliberately absent — those are what trains the model to land on
// canonical iconic dishes. Specifics force exploration.
export const CUISINES = [
  { id: "sichuan",          label: "Sichuan",                  weight: 1.0 },
  { id: "yunnan",           label: "Yunnan",                   weight: 0.7 },
  { id: "cantonese",        label: "Cantonese",                weight: 1.0 },
  { id: "hunan",            label: "Hunan",                    weight: 0.7 },
  { id: "shanghainese",     label: "Shanghainese",             weight: 0.7 },
  { id: "japanese_country", label: "Japanese countryside",     weight: 0.7 },
  { id: "korean",           label: "Korean",                   weight: 1.0 },
  { id: "thai",             label: "Thai",                     weight: 0.9 },
  { id: "burmese",          label: "Burmese",                  weight: 0.7 },
  { id: "filipino",         label: "Filipino",                 weight: 0.8 },
  { id: "vietnamese",       label: "Vietnamese",               weight: 0.9 },
  { id: "indonesian",       label: "Indonesian",               weight: 0.7 },
  { id: "malaysian",        label: "Malaysian",                weight: 0.6 },
  { id: "goan",             label: "Goan",                     weight: 0.6 },
  { id: "kerala",           label: "Keralan",                  weight: 0.6 },
  { id: "bengali",          label: "Bengali",                  weight: 0.6 },
  { id: "punjabi",          label: "Punjabi",                  weight: 0.7 },
  { id: "kashmiri",         label: "Kashmiri",                 weight: 0.5 },
  { id: "persian",          label: "Persian",                  weight: 0.9 },
  { id: "lebanese",         label: "Lebanese",                 weight: 0.9 },
  { id: "turkish",          label: "Turkish",                  weight: 0.8 },
  { id: "moroccan",         label: "Moroccan",                 weight: 0.8 },
  { id: "tunisian",         label: "Tunisian",                 weight: 0.5 },
  { id: "egyptian",         label: "Egyptian",                 weight: 0.5 },
  { id: "ethiopian",        label: "Ethiopian",                weight: 0.7 },
  { id: "senegalese",       label: "Senegalese",               weight: 0.5 },
  { id: "nigerian",         label: "Nigerian",                 weight: 0.5 },
  { id: "south_african",    label: "South African",            weight: 0.5 },
  { id: "peruvian",         label: "Peruvian",                 weight: 0.7 },
  { id: "yucatan",          label: "Yucatán Mexican",          weight: 0.7 },
  { id: "oaxacan",          label: "Oaxacan",                  weight: 0.7 },
  { id: "puerto_rican",     label: "Puerto Rican",             weight: 0.6 },
  { id: "cuban",            label: "Cuban",                    weight: 0.6 },
  { id: "cajun",            label: "Cajun",                    weight: 0.7 },
  { id: "lowcountry",       label: "Lowcountry Southern",      weight: 0.6 },
  { id: "quebecois",        label: "Québécois",                weight: 0.5 },
  { id: "pacific_nw",       label: "Pacific Northwest",        weight: 0.5 },
  { id: "sicilian",         label: "Sicilian",                 weight: 0.7 },
  { id: "northern_italian", label: "Northern Italian",         weight: 0.7 },
  { id: "ligurian",         label: "Ligurian",                 weight: 0.5 },
  { id: "sardinian",        label: "Sardinian",                weight: 0.5 },
  { id: "provencal",        label: "Provençal",                weight: 0.7 },
  { id: "alsatian",         label: "Alsatian",                 weight: 0.5 },
  { id: "burgundian",       label: "Burgundian",               weight: 0.6 },
  { id: "basque",           label: "Basque",                   weight: 0.6 },
  { id: "catalan",          label: "Catalan",                  weight: 0.6 },
  { id: "galician",         label: "Galician",                 weight: 0.5 },
  { id: "andalusian",       label: "Andalusian",               weight: 0.6 },
  { id: "georgian_country", label: "Georgian (country)",       weight: 0.6 },
  { id: "polish",           label: "Polish",                   weight: 0.6 },
  { id: "hungarian",        label: "Hungarian",                weight: 0.6 },
  { id: "russian_country",  label: "Russian countryside",      weight: 0.4 },
  { id: "scandinavian",     label: "Scandinavian",             weight: 0.5 },
  { id: "british_pub",      label: "British pub classic",      weight: 0.5 },
];

// ── Techniques ─────────────────────────────────────────────────────
// `onlyTypes` constrains a technique to types where it actually
// applies — "laminated" only makes sense for baked goods. Techniques
// without `onlyTypes` apply broadly.
export const TECHNIQUES = [
  { id: "smoked",       label: "smoked",                                  weight: 0.7 },
  { id: "cured",        label: "cured",                                   weight: 0.6 },
  { id: "fermented",    label: "fermented",                               weight: 0.7 },
  { id: "pickled",      label: "pickled",                                 weight: 0.7,
    onlyTypes: ["ferment_pickle", "condiment", "salad", "appetizer_small"] },
  { id: "confit",       label: "confit (slow-rendered in fat)",           weight: 0.6 },
  { id: "salt_crusted", label: "salt-crusted",                            weight: 0.4,
    onlyTypes: ["plate_main", "egg_hero"] },
  { id: "en_papillote", label: "cooked en papillote",                     weight: 0.4,
    onlyTypes: ["plate_main", "egg_hero", "plate_side"] },
  { id: "en_croute",    label: "wrapped en croûte",                       weight: 0.4,
    onlyTypes: ["plate_main", "savory_tart"] },
  { id: "hay_baked",    label: "hay-baked",                               weight: 0.3,
    onlyTypes: ["plate_main", "plate_side"] },
  { id: "terrine",      label: "made as a terrine or pâté",               weight: 0.4,
    onlyTypes: ["plate_main", "appetizer_small"] },
  { id: "rillette",     label: "made as a rillette",                      weight: 0.4,
    onlyTypes: ["plate_main", "appetizer_small", "condiment"] },
  { id: "brulee",       label: "brûléed",                                 weight: 0.4,
    onlyTypes: ["baked_sweet", "candy_confection", "plate_main", "appetizer_small"] },
  { id: "hand_rolled",  label: "with hand-rolled dough",                  weight: 0.5,
    onlyTypes: ["plate_main", "dumpling", "baked_sweet", "baked_savory"] },
  { id: "laminated",    label: "with laminated dough",                    weight: 0.4,
    onlyTypes: ["baked_sweet", "baked_savory", "breakfast_pastry"] },
  { id: "stuffed",      label: "stuffed",                                 weight: 0.6 },
  { id: "gratineed",    label: "gratinéed",                               weight: 0.5,
    onlyTypes: ["plate_main", "plate_side", "savory_tart"] },
  { id: "raw_crudo",    label: "served raw / crudo",                      weight: 0.4,
    onlyTypes: ["plate_main", "appetizer_small", "raw_prep", "salad"] },
  { id: "tableside",    label: "with a tableside finish",                 weight: 0.4,
    onlyTypes: ["plate_main", "plate_side", "appetizer_small"] },
  { id: "slow_braised", label: "slow-braised for hours",                  weight: 0.6,
    onlyTypes: ["plate_main", "soup_stew"] },
  { id: "pressure",     label: "pressure-extracted (intensely concentrated)", weight: 0.4,
    onlyTypes: ["soup_stew", "stock_broth", "sauce"] },
  { id: "infused",      label: "infused over days",                       weight: 0.4,
    onlyTypes: ["beverage", "compound_flavoring", "marinade_brine"] },
  { id: "browned_butter", label: "with browned butter as the heart",      weight: 0.5,
    onlyTypes: ["plate_main", "plate_side", "baked_sweet", "cookie", "sauce"] },
  { id: "miso_glazed",  label: "miso-glazed",                             weight: 0.5,
    onlyTypes: ["plate_main", "plate_side", "egg_hero"] },
  { id: "agrodolce",    label: "with an agrodolce sauce",                 weight: 0.4,
    onlyTypes: ["plate_main", "plate_side", "sauce"] },
  { id: "two_textures", label: "built around two contrasting textures",   weight: 0.5 },
  { id: "single_skillet", label: "all-in-one-skillet",                    weight: 0.5,
    onlyTypes: ["plate_main", "egg_hero", "baked_savory"] },
  { id: "no_cook",      label: "with zero cooking",                       weight: 0.3,
    onlyTypes: ["sauce", "condiment", "salad", "raw_prep", "beverage"] },
];

// ── Heroes ─────────────────────────────────────────────────────────
// Pantry deep-cuts Claude underuses by default. Static list; the
// picker also injects signal-based heroes from the user's pantry
// (e.g. an expiring ingredient becomes a forced hero with boosted
// weight) at draw time — see computeSignalHeroes.
export const HEROES = [
  // Spices the model doesn't reach for first
  { id: "sumac",                label: "sumac",                            weight: 0.6 },
  { id: "urfa_biber",           label: "urfa biber",                       weight: 0.5 },
  { id: "aleppo_pepper",        label: "Aleppo pepper",                    weight: 0.5 },
  { id: "ras_el_hanout",        label: "ras el hanout",                    weight: 0.5 },
  { id: "berbere",              label: "berbere",                          weight: 0.4 },
  { id: "gochugaru",            label: "gochugaru",                        weight: 0.6 },
  { id: "shichimi",             label: "shichimi togarashi",               weight: 0.5 },
  { id: "fenugreek",            label: "fenugreek",                        weight: 0.4 },
  { id: "fennel_pollen",        label: "fennel pollen",                    weight: 0.4 },
  { id: "mahlab",               label: "mahlab",                           weight: 0.3 },
  { id: "cardamom_green",       label: "green cardamom",                   weight: 0.5 },
  { id: "cardamom_black",       label: "black cardamom",                   weight: 0.4 },
  { id: "anise_star",           label: "star anise",                       weight: 0.5 },
  { id: "long_pepper",          label: "long pepper",                      weight: 0.3 },
  { id: "grains_of_paradise",   label: "grains of paradise",               weight: 0.3 },
  { id: "asafoetida",           label: "asafoetida",                       weight: 0.4 },
  { id: "nigella",              label: "nigella seed",                     weight: 0.4 },
  // Ferments / preserved
  // ONE miso entry at normal deep-cut weight. Was two (white 0.6 + red
  // 0.5) which handed miso ~2x the probability mass of every other hero —
  // the root of "miso paste in 3+ consecutive recipes." Family rotation
  // (HERO_FAMILY below) capped consecutive repeats but never the inflated
  // base rate. One entry, one weight, in line with the rest of the list.
  { id: "miso_white",           label: "miso",                             weight: 0.45 },
  { id: "doenjang",             label: "doenjang",                         weight: 0.4 },
  { id: "gochujang",            label: "gochujang",                        weight: 0.6 },
  { id: "fish_sauce",           label: "fish sauce",                       weight: 0.6 },
  { id: "anchovy",              label: "anchovy",                          weight: 0.6 },
  { id: "preserved_lemon",      label: "preserved lemon",                  weight: 0.6 },
  { id: "capers",               label: "capers",                           weight: 0.5 },
  { id: "kimchi",               label: "kimchi",                           weight: 0.5 },
  { id: "sauerkraut",           label: "sauerkraut",                       weight: 0.4 },
  // Dairy deep cuts
  { id: "browned_butter",       label: "browned butter",                   weight: 0.5 },
  { id: "cultured_butter",      label: "cultured butter",                  weight: 0.4 },
  { id: "creme_fraiche",        label: "crème fraîche",                    weight: 0.5 },
  { id: "labneh",               label: "labneh",                           weight: 0.5 },
  { id: "ricotta_salata",       label: "ricotta salata",                   weight: 0.4 },
  { id: "feta",                 label: "feta",                             weight: 0.5 },
  { id: "halloumi",             label: "halloumi",                         weight: 0.5 },
  { id: "gjetost",              label: "gjetost",                          weight: 0.3 },
  { id: "burrata",              label: "burrata",                          weight: 0.5 },
  // Alliums / aromatics
  { id: "black_garlic",         label: "black garlic",                     weight: 0.5 },
  { id: "garlic_confit",        label: "garlic confit",                    weight: 0.5 },
  { id: "leek_ash",             label: "leek ash",                         weight: 0.3 },
  { id: "shallot_crispy",       label: "crispy fried shallot",             weight: 0.4 },
  { id: "ginger_pickled",       label: "pickled ginger",                   weight: 0.4 },
  // Pantry deep-cuts
  { id: "tahini",               label: "tahini",                           weight: 0.6 },
  { id: "kombu",                label: "kombu",                            weight: 0.4 },
  { id: "bonito",               label: "bonito flakes",                    weight: 0.4 },
  { id: "yuzu",                 label: "yuzu",                             weight: 0.5 },
  { id: "pomegranate_molasses", label: "pomegranate molasses",             weight: 0.5 },
  { id: "tamarind",             label: "tamarind",                         weight: 0.5 },
  { id: "sherry_vinegar",       label: "sherry vinegar",                   weight: 0.4 },
  { id: "rice_vinegar_seasoned", label: "seasoned rice vinegar",           weight: 0.4 },
  // Flavor profiles (vibe-style heroes)
  { id: "smoky",                label: "a deeply smoky profile",           weight: 0.4 },
  { id: "fermented_tart",       label: "a fermented-tart profile",         weight: 0.4 },
  { id: "umami_bomb",           label: "a quadruple-umami stack",          weight: 0.4 },
  { id: "bittersweet_caramel",  label: "bittersweet caramel notes",        weight: 0.4 },
  { id: "bright_acid",          label: "an aggressive acid kick",          weight: 0.4 },
  { id: "herbal_grassy",        label: "fresh herbal-grassy character",    weight: 0.4 },
];

// ── Vibes ──────────────────────────────────────────────────────────
export const VIBES = [
  { id: "late_night",              label: "late-night kitchen",                 weight: 0.6 },
  { id: "sunday_project",          label: "Sunday-morning project",             weight: 0.6 },
  { id: "hangover",                label: "hangover-morning",                   weight: 0.4 },
  { id: "thunderstorm",            label: "thunderstorm comfort",               weight: 0.4 },
  { id: "heat_wave",               label: "heat-wave cool",                     weight: 0.4 },
  { id: "cold_snap",               label: "cold-snap warming",                  weight: 0.4 },
  { id: "showstopper",             label: "dinner-party showstopper",           weight: 0.6 },
  { id: "picnic",                  label: "picnic-friendly",                    weight: 0.4 },
  { id: "make_ahead",              label: "make-ahead lunch",                   weight: 0.4 },
  { id: "solo",                    label: "solo cook for one",                  weight: 0.4 },
  { id: "two_person_plate",        label: "intimate two-person plate",          weight: 0.4 },
  { id: "kid_friendly",            label: "kid-friendly without dumbing down",  weight: 0.3 },
  { id: "surprising_savory_sweet", label: "surprising savory-sweet crossover", weight: 0.4 },
];

// ── Sampler ────────────────────────────────────────────────────────
// Probability of each optional axis being included on a draw. Type
// is always rolled; the rest are coin-flips with these weights.
const AXIS_ROLL = {
  cuisine:   0.55,
  technique: 0.45,
  hero:      0.40,
  vibe:      0.25,
};

// Weighted pick from an array of `{ id, weight }`. Excludes any id
// in `excludeIds`. Returns null when no candidate remains.
function weightedPick(items, excludeIds = [], boostIds = []) {
  const eligible = items.filter((it) => !excludeIds.includes(it.id));
  if (!eligible.length) return null;
  // Rating signal (A5): territories the user has NAILED draw more
  // often — a nudge (×1.75), never a lock, so the picker still
  // explores. Exclusion (anti-repetition) beats boost by running
  // first: a loved-but-just-cooked cuisine still rotates out.
  const boost = new Set(boostIds || []);
  const weightOf = (it) => (it.weight || 1) * (boost.has(it.id) ? 1.75 : 1);
  const total = eligible.reduce((s, it) => s + weightOf(it), 0);
  let r = Math.random() * total;
  for (const it of eligible) {
    r -= weightOf(it);
    if (r <= 0) return it;
  }
  return eligible[eligible.length - 1];
}

// Coin flip — returns true with probability p.
const roll = (p) => Math.random() < p;

// Compute signal-based heroes from current pantry + cookHistory.
// These are dynamically generated per draw, NOT picked from the static
// HEROES list, so the draw can react to "you have heavy cream that
// expires in 2 days" by suggesting a dairy-hero frame.
function computeSignalHeroes({ pantry = [] }) {
  const out = [];
  const now = Date.now();
  const fiveDays = 5 * 24 * 60 * 60 * 1000;
  const expiring = pantry.filter((p) => {
    const t = p.expiresAt ? +new Date(p.expiresAt) : NaN;
    return Number.isFinite(t) && t > now && t - now < fiveDays;
  });
  if (expiring.length > 0) {
    const pick = expiring[Math.floor(Math.random() * expiring.length)];
    const name = pick.name || pick.canonicalId || "an expiring ingredient";
    out.push({
      // unique id per draw so it doesn't collide with static heroes
      id: `signal_expiring_${pick.canonicalId || pick.ingredientId || pick.id || "x"}`,
      label: `${name} (expires soon — make IT the hero, not a side player)`,
      weight: 1.5, // boosted: expiry-rescue is high-value
    });
  }
  return out;
}

// Build the prompt directive that gets injected into the edge fn's
// HARD CONSTRAINT block. Multi-line so each axis renders cleanly.
function buildPromptDirective(frame) {
  const parts = [`TODAY'S CONCEPT: ${frame.label}.`];
  if (frame.type)       parts.push(`Recipe type: ${frame.type.label}`);
  if (frame.cuisine)    parts.push(`Cuisine: ${frame.cuisine.label}`);
  if (frame.technique)  parts.push(`Technique anchor: ${frame.technique.label}`);
  if (frame.hero)       parts.push(`Hero element: ${frame.hero.label}`);
  if (frame.vibe)       parts.push(`Vibe: ${frame.vibe.label}`);
  if (frame.fixedDishName) parts.push(`Specific dish (locked): ${frame.fixedDishName}`);
  return parts.join("\n  ");
}

// User-facing label. Picks a sentence shape based on which axes
// landed. Designed to read like an editor's brief, not a prompt.
function buildLabel(frame) {
  const { type, cuisine, technique, hero, vibe } = frame;
  const typeWord = (type?.label || "").replace(/^A /, "").replace(/^An /, "").toLowerCase();
  if (type && cuisine && hero) {
    return `A ${cuisine.label} ${typeWord} built around ${hero.label}`;
  }
  if (type && cuisine && technique) {
    return `A ${cuisine.label} ${typeWord}, ${technique.label}`;
  }
  if (type && cuisine) {
    return `A ${cuisine.label} take on ${typeWord}`;
  }
  if (type && technique && hero) {
    return `${type.label}, ${technique.label}, anchored by ${hero.label}`;
  }
  if (type && technique) {
    return `${type.label}, ${technique.label}`;
  }
  if (type && hero) {
    return `${type.label}, hero: ${hero.label}`;
  }
  if (type && vibe) {
    return `${type.label} for ${vibe.label}`;
  }
  if (type) return type.label;
  if (cuisine && hero) return `Something ${cuisine.label}, anchored by ${hero.label}`;
  if (cuisine) return `Something ${cuisine.label}`;
  if (technique) return `Something ${technique.label}`;
  if (hero) return `Something built around ${hero.label}`;
  return "Something surprising";
}

// Deterministic short hash of the populated axes — used for the
// frame's id. Same axes always produce the same id, so recent-frame
// de-duplication works across reloads.
function frameId(frame) {
  const ids = [
    frame.type?.id || "",
    frame.cuisine?.id || "",
    frame.technique?.id || "",
    frame.hero?.id || "",
    frame.vibe?.id || "",
    frame.fixedDishName || "",
  ].join("|");
  let h = 0;
  for (let i = 0; i < ids.length; i++) {
    h = (h * 31 + ids.charCodeAt(i)) | 0;
  }
  return `frame_${(h >>> 0).toString(36)}`;
}

// Main entry point. Pick a frame.
//
// Modes:
//   "open" / "freeform" / "family" / "chefs_choice" — full-axis draw
//   "specific" — `fixedDishName` is required; type & cuisine are
//                skipped, only technique + hero rolled (sub-frame)
//   "pantry"   — pantry-only mode; bias against types that need
//                specialty inputs not typically in stock (frozen,
//                pastry, etc.)
//
// `recentFrameIds` excludes recently-drawn frames (a single retry
// is performed if the first draw collides with the recent set).
// `cookHistory.topCuisines` triggers cuisine anti-rotation — drop
// the top 3 from candidate cuisines.
export function pickConceptFrame({
  recentFrameIds = [],
  pantry = [],
  mode = "open",
  cookHistory = null,
  fixedDishName = null,
  excludeHeroIds = [],
  retryDepth = 0,
} = {}) {
  const heroExclude = new Set(excludeHeroIds || []);
  const frame = {
    type: null, cuisine: null, technique: null, hero: null, vibe: null,
  };

  // SPECIFIC tier — user named the dish. Skip type/cuisine, only roll
  // technique + hero so the dish gets an interesting sub-anchor.
  if (fixedDishName) {
    if (roll(0.6)) {
      // No type to filter against — pick any unrestricted technique.
      frame.technique = weightedPick(TECHNIQUES.filter((t) => !t.onlyTypes));
    }
    if (roll(0.6)) {
      // Hero on a NAMED dish: pantry SIGNALS only (an expiring ingredient
      // worth using up) — NEVER the global exotic HEROES list, in ANY
      // mode. When the user typed a specific dish ("chicken sauce"),
      // stapling a random deep-cut like miso or preserved lemon onto it is
      // exactly the "why is there miso in my chicken" problem. Random
      // provocation belongs to OPEN / SURPRISE drafts (the full-frame draw
      // below), not to dishes the user named. A named dish gets its
      // DIRECTION from the clarify questions — chosen by the user, fitting
      // the protein/cuisine — instead of silently rolled.
      const signalHeroes = computeSignalHeroes({ pantry });
      if (signalHeroes.length > 0) frame.hero = weightedPick(signalHeroes);
    }
    frame.fixedDishName = fixedDishName;
    frame.label = buildLabel(frame) + ` for ${fixedDishName}`;
    frame.id = frameId(frame);
    frame.promptDirective = buildPromptDirective(frame);
    return frame;
  }

  // Full-frame draw — type first (always populated), then optional axes.
  let typeCandidates = RECIPE_TYPES.slice();
  if (mode === "pantry") {
    typeCandidates = typeCandidates.filter((t) => !PANTRY_INCOMPATIBLE_TYPES.has(t.id));
  }
  frame.type = weightedPick(typeCandidates);

  // Cuisine anti-rotation: drop the user's top-3 recent cuisines.
  const recentCuisineIds = (cookHistory?.topCuisines || [])
    .slice(0, 3)
    .map((c) => c.id || c);
  // Rating steering: cuisines the user has rated "nailed" get a
  // weight boost (see weightedPick) — loved territory surfaces more.
  const lovedCuisineIds = (cookHistory?.lovedCuisines || []).map((c) => c.id || c);

  if (roll(AXIS_ROLL.cuisine)) {
    frame.cuisine = weightedPick(CUISINES, recentCuisineIds, lovedCuisineIds);
  }
  if (roll(AXIS_ROLL.technique)) {
    const techCandidates = TECHNIQUES.filter(
      (t) => !t.onlyTypes || t.onlyTypes.includes(frame.type?.id),
    );
    frame.technique = weightedPick(techCandidates);
  }
  if (roll(AXIS_ROLL.hero)) {
    // Pantry-only mode: hero pool MUST be drawn from pantry signals
    // only, never the static HEROES list. The static list contains
    // ingredients like gochujang, miso, preserved lemon — anchoring
    // a recipe around them in pantry-only mode forces the AI to
    // include shopping items the user explicitly said they don't
    // want. The signal heroes are derived from the user's actual
    // pantry (expiring items), so they always respect the contract.
    // If signal heroes is empty, hero just doesn't fire — pantry
    // mode is utility, not provocation.
    const pool = mode === "pantry"
      ? computeSignalHeroes({ pantry })
      : [...HEROES, ...computeSignalHeroes({ pantry })];
    // Hero-level anti-rotation (family-expanded): a hero the user saw
    // in the last N drafts — or its near-duplicate sibling — is out
    // of the pool entirely, not just deprioritized. Falls back to the
    // full pool only if rotation would empty it.
    const heroes = pool.filter((h) => !heroExclude.has(h.id));
    const pick = heroes.length > 0 ? heroes : pool;
    if (pick.length > 0) frame.hero = weightedPick(pick);
  }
  if (roll(AXIS_ROLL.vibe)) {
    frame.vibe = weightedPick(VIBES);
  }

  frame.label = buildLabel(frame);
  frame.id = frameId(frame);
  frame.promptDirective = buildPromptDirective(frame);

  // Single retry if the draw collided with the recent set.
  if (retryDepth < 1 && recentFrameIds.includes(frame.id)) {
    return pickConceptFrame({
      recentFrameIds, pantry, mode, cookHistory, fixedDishName,
      excludeHeroIds, retryDepth: retryDepth + 1,
    });
  }
  return frame;
}

// Variant: pick a frame with `type` pinned. Used by the CHANGE TYPE
// flow on the reveal screen — the user picks a recipe type, then we
// re-roll the rest.
export function pickConceptFrameWithType({
  typeId,
  recentFrameIds = [],
  pantry = [],
  mode = "open",
  cookHistory = null,
  excludeHeroIds = [],
}) {
  const heroExclude = new Set(excludeHeroIds || []);
  const fixed = RECIPE_TYPES.find((t) => t.id === typeId);
  if (!fixed) return pickConceptFrame({ recentFrameIds, pantry, mode, cookHistory, excludeHeroIds });

  const frame = { type: fixed, cuisine: null, technique: null, hero: null, vibe: null };
  const recentCuisineIds = (cookHistory?.topCuisines || []).slice(0, 3).map((c) => c.id || c);
  const lovedCuisineIds = (cookHistory?.lovedCuisines || []).map((c) => c.id || c);

  if (roll(AXIS_ROLL.cuisine)) {
    frame.cuisine = weightedPick(CUISINES, recentCuisineIds, lovedCuisineIds);
  }
  if (roll(AXIS_ROLL.technique)) {
    const techCandidates = TECHNIQUES.filter(
      (t) => !t.onlyTypes || t.onlyTypes.includes(fixed.id),
    );
    frame.technique = weightedPick(techCandidates);
  }
  if (roll(AXIS_ROLL.hero)) {
    // Same pantry-mode hero gate as pickConceptFrame above — never
    // pull from static HEROES when the user is in pantry-only mode.
    const pool = mode === "pantry"
      ? computeSignalHeroes({ pantry })
      : [...HEROES, ...computeSignalHeroes({ pantry })];
    const heroes = pool.filter((h) => !heroExclude.has(h.id));
    const pick = heroes.length > 0 ? heroes : pool;
    if (pick.length > 0) frame.hero = weightedPick(pick);
  }
  if (roll(AXIS_ROLL.vibe)) {
    frame.vibe = weightedPick(VIBES);
  }

  frame.label = buildLabel(frame);
  frame.id = frameId(frame);
  frame.promptDirective = buildPromptDirective(frame);
  return frame;
}

// ── Recent-frame tracking ──────────────────────────────────────────
// Persists last 30 frame ids per user in localStorage so anti-
// rotation survives page reloads. A best-effort store; quota or
// private-mode failures are non-fatal.
const RECENT_FRAMES_KEY = (uid) => `ai.frames.recent.v1:${uid}`;
const RECENT_FRAMES_LIMIT = 30;

// Per-axis anti-rotation: tracks recent HERO ids independently of
// frame ids. Why: frame anti-rotation hashes the entire axis tuple
// (type|cuisine|technique|hero|vibe), so two consecutive draws can
// land on the SAME hero (e.g. miso_white) as long as any other axis
// differs. Combined with two miso entries in HEROES (white + red),
// the user observed miso paste appearing in 3+ consecutive recipes
// for unrelated dishes. Hero-level rotation kills that pattern at
// the source. 12 entries deep — long enough to space out repeats,
// short enough that the full hero pool eventually re-cycles.
const RECENT_HEROES_KEY = (uid) => `ai.heroes.recent.v1:${uid}`;
const RECENT_HEROES_LIMIT = 12;

export function readRecentFrameIds(userId) {
  if (typeof window === "undefined" || !userId) return [];
  try {
    const raw = window.localStorage?.getItem(RECENT_FRAMES_KEY(userId));
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

export function pushRecentFrameId(userId, id) {
  if (typeof window === "undefined" || !userId || !id) return;
  try {
    const cur = readRecentFrameIds(userId);
    const next = [id, ...cur.filter((x) => x !== id)].slice(0, RECENT_FRAMES_LIMIT);
    window.localStorage?.setItem(RECENT_FRAMES_KEY(userId), JSON.stringify(next));
  } catch { /* quota / private mode — non-fatal */ }
}

// Tally the viewer's cooked cuisines (cook_log.recipeCuisine, free
// text) into the { topCuisines: [{id, count}] } shape pickConceptFrame
// anti-rotates against. Normalizes to CUISINES-style slugs so
// "Japanese country" matches japanese_country. Most-cooked first.
export function topCuisinesFromCookLogs(logs) {
  const counts = new Map();
  for (const l of logs || []) {
    const c = String(l?.recipeCuisine || l?.cuisine || "")
      .toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
    if (!c) continue;
    counts.set(c, (counts.get(c) || 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([id, count]) => ({ id, count }));
}

// Cuisines the user rated "nailed" — the positive counterpart to the
// anti-rotation tally above. Feeds cookHistory.lovedCuisines so
// pickConceptFrame boosts loved territory (weightedPick ×1.75).
export function lovedCuisinesFromCookLogs(logs) {
  const counts = new Map();
  for (const l of logs || []) {
    if ((l?.rating || null) !== "nailed") continue;
    const c = String(l?.recipeCuisine || l?.cuisine || "")
      .toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
    if (!c) continue;
    counts.set(c, (counts.get(c) || 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([id, count]) => ({ id, count }));
}

export function readRecentHeroIds(userId) {
  if (typeof window === "undefined" || !userId) return [];
  try {
    const raw = window.localStorage?.getItem(RECENT_HEROES_KEY(userId));
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

export function pushRecentHeroId(userId, heroId) {
  if (typeof window === "undefined" || !userId || !heroId) return;
  try {
    const cur = readRecentHeroIds(userId);
    const next = [heroId, ...cur.filter((x) => x !== heroId)].slice(0, RECENT_HEROES_LIMIT);
    window.localStorage?.setItem(RECENT_HEROES_KEY(userId), JSON.stringify(next));
  } catch { /* quota / private mode — non-fatal */ }
}

// Family-level rotation: white miso and red miso are different IDs
// but Claude treats both as "miso" in the prompt. Same for any
// future near-duplicates. Mapping a family key onto each hero so
// rotation excludes the WHOLE family, not just the specific id.
const HERO_FAMILY = {
  miso_white: "miso",
  miso_red:   "miso",
};
function heroFamily(heroId) {
  return HERO_FAMILY[heroId] || heroId;
}
// Build the exclusion set from recent hero ids — expands each id to
// its family so a recent miso_white blocks miso_red on the next pick.
export function recentHeroFamilyExclusions(userId) {
  const recent = readRecentHeroIds(userId);
  const families = new Set(recent.map(heroFamily));
  const excluded = [];
  for (const h of HEROES) {
    if (families.has(heroFamily(h.id))) excluded.push(h.id);
  }
  return excluded;
}
